import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, YoutubeLogo, MagnifyingGlass } from "@phosphor-icons/react";
import { PageContainer, EmptyState } from "@/components/customer/PageContainer";
import { api } from "@/lib/api";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/videos").then(({ data }) => setVideos(data)).catch(() => setVideos([])).finally(() => setLoading(false));
  }, []);

  const filtered = videos.filter((v) => {
    if (!q) return true;
    const hay = `${v.title} ${v.description} ${v.category} ${v.model} ${(v.tags || []).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <PageContainer
      overline="Tutorials"
      title="Video Tutorials"
      subtitle="Step-by-step video guides for your On The Move caravan's systems, hosted on YouTube."
      actions={
        <div className="flex items-center gap-2 bg-[#141414] border border-neutral-800 rounded-full px-4 py-2 w-64 focus-within:border-[#73bf44] transition-colors">
          <MagnifyingGlass size={16} className="text-neutral-500" />
          <input
            data-testid="video-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search videos..."
            className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600"
          />
        </div>
      }
    >
      {loading ? (
        <div className="text-neutral-600">Loading...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={YoutubeLogo} title="No videos available yet" hint="Your administrator can add YouTube tutorials from the admin backend." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((v, i) => (
            <motion.a
              key={v.id}
              href={v.youtube_url}
              target="_blank"
              rel="noreferrer"
              data-testid="video-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="group bg-[#141414] border border-neutral-800 hover:border-[#73bf44] rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative aspect-video bg-black overflow-hidden">
                {v.thumbnail ? (
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-700">
                    <YoutubeLogo size={40} />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#73bf44] transition-colors">
                    <PlayCircle size={34} weight="fill" className="text-white group-hover:text-[#050505] transition-colors" />
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="font-heading font-semibold text-white leading-tight mb-1">{v.title}</div>
                {v.description && <div className="text-xs text-neutral-500 line-clamp-2">{v.description}</div>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {v.category && (
                    <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-[#73bf44]/10 text-[#73bf44] border border-[#73bf44]/30">{v.category}</span>
                  )}
                  {v.model && (
                    <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-black/40 border border-neutral-700 text-neutral-400">{v.model}</span>
                  )}
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
